import { useCallback, useEffect, useMemo, useState } from "react";
import { Binoculars, GearSix, PaperPlaneTilt, SunHorizon } from "@phosphor-icons/react";
import { motion } from "motion/react";
import { Redirect, Route, Switch, useLocation, useSearch } from "wouter";
import type { Billing, Onboarding, ProfileView, Stats } from "../shared/types.ts";
import { Landing } from "./landing/Landing.tsx";
import { Button, EmptyState, MarkLoader, Sheet, Shell, Toaster, useToast } from "./ui/index.ts";
import { configureApi, ERROR_TEXT, getBilling, getMe, getProfile, getStats } from "./lib/api.ts";
import { flushRef } from "./lib/ref.ts";
import { confettiFrom } from "./lib/confetti.ts";
import { Ctx, type AppCtx } from "./lib/ctx.ts";
import { NEEDS_HASH, useEngineEvents } from "./lib/events.ts";
import { nextOf, takeNext } from "./lib/next.ts";
import { pickedPlan } from "./lib/plan.ts";
import { accessToken, getSession, logOut, onAuthStateChange } from "./lib/supabase.ts";
import { AlertEdit } from "./screens/AlertEdit.tsx";
import { Applications } from "./screens/Applications.tsx";
import { Auth } from "./screens/Auth.tsx";
import { Careers } from "./screens/Careers.tsx";
import { Home } from "./screens/Home.tsx";
import { Jobs } from "./screens/Jobs.tsx";
import { Legal } from "./screens/Legal.tsx";
import { NeedsYou, useNeedsYou, waitsOnYou } from "./screens/NeedsYou.tsx";
import { NotFound } from "./screens/NotFound.tsx";
import { SKIP_KEY, Welcome } from "./screens/onboarding/Welcome.tsx";
import { ProfileScreen } from "./screens/Profile.tsx";
import { PublicJobs } from "./screens/PublicJobs.tsx";
import { ForgotPassword, ResetPassword } from "./screens/ResetPassword.tsx";
import { Upgrade } from "./screens/settings/Upgrade.tsx";
import { Unsubscribe } from "./screens/Unsubscribe.tsx";
import { UpgradeModal } from "./screens/UpgradeModal.tsx";

/** `name` is the sign-in provider's, such as the Google account's, when it gave one. */
type User = { id: string; email: string; name?: string };

const APP_PATHS = /^\/(welcome|jobs|applications|settings|profile|upgrade)(\/|$)/;
const LEGAL = /^\/(privacy|terms)\/?$/;
/** Open signed in or out, outside the job-seeker app, so an applicant is never sent to onboarding from here. */
const CAREERS = /^\/careers(\/|$)/;
const AUTH_PATHS = /^\/(login|signup)\/?$/;
/** Pages an email links to, the same signed in or out. */
const EMAIL_PAGES = { "/forgot-password": ForgotPassword, "/reset-password": ResetPassword, "/unsubscribe": Unsubscribe, "/alerts": AlertEdit } as const;

/**
 * Landing and the auth screens while signed out, the app once Supabase has a session. A 401 from the API signs out. A
 * safe `?next=` on the auth screens, or the one remembered before Google sign-in, opens once signed in.
 */
export function App() {
  const [location] = useLocation();
  const search = useSearch();
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [after, setAfter] = useState(takeNext);

  useEffect(() => {
    const take = (s: { user: { id: string; email?: string; user_metadata?: { full_name?: unknown; name?: unknown } } } | null) =>
      setUser((u) => {
        if (!s) return null;
        if (u?.id === s.user.id) return u;
        const meta = s.user.user_metadata;
        const name = [meta?.full_name, meta?.name].find((n): n is string => typeof n === "string" && !!n.trim());
        return { id: s.user.id, email: s.user.email ?? "", name };
      });
    configureApi({
      token: accessToken,
      onSignedOut: () => {
        logOut().catch(() => {});
        setUser(null);
      },
    });
    getSession().then(take, () => setUser(null));
    return onAuthStateChange((_, s) => take(s));
  }, []);

  useEffect(() => {
    if (user === null || (after && location === after.split("?")[0])) setAfter(null);
  }, [user, after, location]);

  const target = user ? ((AUTH_PATHS.test(location) ? nextOf(search) : null) ?? after) : null;
  if (target && target.split("?")[0] !== location) return <Redirect to={target} replace />;
  const EmailPage = EMAIL_PAGES[location.replace(/\/$/, "") as keyof typeof EMAIL_PAGES];

  return (
    <>
      {EmailPage ? (
        <EmailPage />
      ) : CAREERS.test(location) ? (
        <Careers user={user} />
      ) : LEGAL.test(location) ? (
        <Legal page={location.startsWith("/privacy") ? "privacy" : "terms"} />
      ) : user === undefined ? (
        <div className="grid min-h-dvh place-items-center">
          <MarkLoader delay={0.4} />
        </div>
      ) : user ? <SignedIn key={user.id} user={user} /> : <SignedOut />}
      <Toaster />
    </>
  );
}

/**
 * Signed out, an app page sends to login and comes back to it, query and all, once signed in. Bare `/jobs` is the public
 * jobs page instead.
 */
function SignedOut() {
  const [location, navigate] = useLocation();
  const search = useSearch();
  return (
    <Switch>
      <Route path="/">
        <Landing onStart={() => navigate("/signup")} onLogIn={() => navigate("/login")} />
      </Route>
      <Route path="/login">
        <Auth mode="login" />
      </Route>
      <Route path="/signup">
        <Auth mode="signup" />
      </Route>
      <Route path="/jobs">
        <PublicJobs />
      </Route>
      <Route>
        {APP_PATHS.test(location) ? <Redirect to={`/login?next=${encodeURIComponent(location + (search ? `?${search}` : ""))}`} replace /> : <NotFound standalone />}
      </Route>
    </Switch>
  );
}

function SignedIn({ user }: { user: User }) {
  const [location, navigate] = useLocation();
  const search = useSearch();
  const { toast } = useToast();
  const [stats, setStats] = useState<Stats | null>(null);
  const [profile, setProfile] = useState<ProfileView | null>(null);
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [down, setDown] = useState(false);
  const [needsOpen, setNeedsOpen] = useState(false);
  const [needsFirst, setNeedsFirst] = useState<string | null>(null);
  const [billing, setBilling] = useState<Billing | null>(null);
  const blocked = useNeedsYou();
  const needs = useMemo(() => blocked.rows.filter(waitsOnYou), [blocked.rows]);

  const refreshStats = useCallback(() => {
    getStats().then(setStats, () => {});
  }, []);

  const loadMe = useCallback(() => getMe().then((m) => setOnboarding(m.onboarding)), []);

  const start = useCallback(() => {
    setDown(false);
    refreshStats();
    getBilling().then(setBilling, () => {});
    Promise.all([getProfile(), loadMe()]).then(
      ([p]) => {
        setProfile(p);
        flushRef();
      },
      () => setDown(true),
    );
  }, [refreshStats, loadMe]);

  useEffect(() => {
    start();
    const t = setInterval(refreshStats, 60_000);
    return () => clearInterval(t);
  }, [start, refreshStats]);

  useEngineEvents(toast, refreshStats);

  useEffect(() => {
    const go = () => {
      if (window.location.hash !== NEEDS_HASH) return;
      history.replaceState(history.state, "", window.location.pathname + window.location.search);
      setNeedsOpen(true);
    };
    go();
    addEventListener("hashchange", go);
    return () => removeEventListener("hashchange", go);
  }, []);

  // An email's `/applications?needs=<job id>` opens the sheet with that one on top, once the list has loaded.
  useEffect(() => {
    const q = new URLSearchParams(search);
    if (location !== "/applications" || !q.has("needs") || !blocked.loaded) return;
    const id = q.get("needs") || null;
    navigate("/applications", { replace: true });
    if (id && !needs.some((n) => n.job.id === id)) toast("This application no longer needs your answer.");
    setNeedsFirst(id);
    if (needs.length) setNeedsOpen(true);
  }, [location, search, blocked.loaded, needs, navigate, toast]);

  // Closes the sheet once its last one is answered, after that row has gone.
  useEffect(() => {
    if (!needsOpen || needs.length) return;
    const t = setTimeout(() => setNeedsOpen(false), 400);
    return () => clearTimeout(t);
  }, [needsOpen, needs.length]);

  const goal = stats?.dailyGoal ?? profile?.dailyGoal ?? 10;
  const appliedToday = stats?.appliedToday ?? 0;

  // A limit toast from anywhere, such as a refused retry, links to the plans while there is one to move to.
  const toastWith = useCallback<AppCtx["toast"]>(
    (text, tone, link) => toast(text, tone, link ?? (billing?.plan !== "max" && (text === ERROR_TEXT.plan_limit || text === ERROR_TEXT.daily_cap) ? { href: "/upgrade", label: "Upgrade" } : undefined)),
    [toast, billing?.plan],
  );

  const signOut = useCallback(() => {
    logOut().finally(() => navigate("/", { replace: true }));
  }, [navigate]);

  const ctx = useMemo<AppCtx>(
    () => ({
      user,
      stats,
      goal,
      profile,
      onboarding,
      setProfile: (p) => {
        setProfile(p);
        loadMe().catch(() => {});
      },
      refreshStats,
      applied: (from, auto) => {
        const n = appliedToday + 1;
        setStats(
          (s) =>
            s && {
              ...s,
              appliedToday: s.appliedToday + 1,
              appliedTotal: s.appliedTotal + 1,
              applications: auto ? { ...s.applications, queued: s.applications.queued + 1 } : s.applications,
            },
        );
        if (n % 5 === 0 || n === goal) confettiFrom(from, n === goal ? 120 : 70);
      },
      unapplied: (auto) => {
        setStats(
          (s) =>
            s && {
              ...s,
              appliedToday: Math.max(0, s.appliedToday - 1),
              appliedTotal: Math.max(0, s.appliedTotal - 1),
              applications: auto ? { ...s.applications, queued: Math.max(0, s.applications.queued - 1) } : s.applications,
            },
        );
      },
      needs,
      needsSent: blocked.sent,
      showNeeds: () => setNeedsOpen(true),
      toast: toastWith,
      signOut,
    }),
    [user, stats, goal, profile, onboarding, loadMe, refreshStats, appliedToday, needs, blocked.sent, toastWith, signOut],
  );

  const who = `${profile?.firstName ?? ""} ${profile?.lastName ?? ""}`.trim() || user.name || user.email.slice(0, 1);
  const nav = [
    { href: "/", label: "Today", icon: SunHorizon },
    { href: "/jobs", label: "Jobs", icon: Binoculars },
    { href: "/applications", label: "Applications", icon: PaperPlaneTilt },
    { href: "/settings", label: "Settings", icon: GearSix },
  ];

  if (location === "/welcome" && !down) {
    return (
      <Ctx.Provider value={ctx}>
        <Welcome />
      </Ctx.Provider>
    );
  }
  if (onboarding && !onboarding.done && !skipped()) return <Redirect to="/welcome" replace />;
  if (onboarding && pickedPlan() && location !== "/settings/billing") return <Redirect to="/settings/billing" replace />;

  return (
    <Ctx.Provider value={ctx}>
      <Shell
        nav={nav}
        user={{ email: user.email, name: who }}
        onSignOut={signOut}
        needs={{ count: needs.length, open: ctx.showNeeds }}
        upgrade={billing?.plan === "free" ? "/upgrade" : undefined}
      >
        {down ? (
          <EmptyState
            title="headcount didn't load."
            text="Check your connection and try again."
            action={
              <Button variant="secondary" onClick={start}>
                Try again
              </Button>
            }
          />
        ) : (
          <motion.div
            key={location.split("/")[1] || "/"}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
          >
            <Switch>
              <Route path="/" component={Home} />
              <Route path="/jobs/saved" component={Jobs} />
              <Route path="/jobs" component={Jobs} />
              <Route path="/applications" component={Applications} />
              <Route path="/upgrade" component={Upgrade} />
              <Route path="/settings/:section?" component={ProfileScreen} />
              <Route path="/profile">
                <Redirect to="/settings" replace />
              </Route>
              <Route path="/signup">
                <Redirect to="/welcome" replace />
              </Route>
              <Route path="/login">
                <Redirect to="/" replace />
              </Route>
              <Route>
                <NotFound />
              </Route>
            </Switch>
          </motion.div>
        )}
      </Shell>
      <Sheet open={needsOpen} onOpenChange={setNeedsOpen} title="Needs you.">
        <NeedsYou rows={needs} onSent={blocked.sent} heading={false} first={needsFirst} />
      </Sheet>
      <UpgradeModal plan={billing?.plan ?? null} />
    </Ctx.Provider>
  );
}

/** Whether the user left setup in this tab; until then an unfinished profile opens /welcome. */
function skipped(): boolean {
  try {
    return sessionStorage.getItem(SKIP_KEY) === "1";
  } catch {
    return true;
  }
}
